import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import React from "react";
import { useFieldArray } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Combobox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";
import * as clientsApi from "@/pages/clients/clients.api";
import {
  TClientExtended,
  TClientExtendedResponse,
} from "@/pages/clients/clients.types";
import * as usersApi from "@/pages/users/users.api";
import { TUser } from "@/pages/users/users.types";

import { useProjects } from "./projects.hooks";

export const ProjectsPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();
  const {
    projectForm,
    handleSubmit,
    isCreating,
  } = useProjects(clientId);

  // Buscar dados do cliente para exibir o nome
  const { data: client } = useQuery<TClientExtendedResponse>({
    queryKey: ["client", clientId],
    queryFn: () => clientsApi.getClientById(clientId!),
    enabled: !!clientId,
    staleTime: 60_000,
  });

  // Parceiros participantes: sempre sub-parceiros da organização que está criando o projeto
  const { data: allClients = [] } = useQuery<TClientExtended[]>({
    queryKey: ["sub-partners", clientId],
    queryFn: () => clientsApi.getSubPartners(clientId!),
    enabled: !!clientId,
    staleTime: 60_000,
  });

  // Buscar todos os usuários para seleção no Hunter
  const { data: allUsers = [] } = useQuery<TUser[]>({
    queryKey: ["users"],
    queryFn: () => usersApi.getAllUsers(),
    staleTime: 60_000,
  });

  // useFieldArray para usuários comerciais
  const {
    fields: userCommercialsFields,
    append: appendUserCommercial,
    remove: removeUserCommercial,
  } = useFieldArray({
    control: projectForm.control,
    name: "user_commercials",
  });

  // useFieldArray para hunters
  const {
    fields: huntersFields,
    append: appendHunter,
    remove: removeHunter,
  } = useFieldArray({
    control: projectForm.control,
    name: "hunters",
  });

  // Usuários disponíveis para hierarquia comercial
  const availableUsers = React.useMemo(() => {
    const selectedUserIds = userCommercialsFields.map((field) => field.id);
    return allUsers
      .filter((user) => !selectedUserIds.includes(user.id))
      .map((user) => ({
        value: user.id,
        label: `${user.first_name} ${user.last_name}${user.email ? ` (${user.email})` : ""}`,
      }));
  }, [allUsers, userCommercialsFields]);

  // Estado de busca e lista ordenada para parceiros participantes
  const [partnerSearch, setPartnerSearch] = React.useState("");

  const sortedPartners = React.useMemo(() => {
    return allClients
      .filter((c) => c.id !== clientId)
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
      .filter((c) => c.name.toLowerCase().includes(partnerSearch.toLowerCase()));
  }, [allClients, clientId, partnerSearch]);

  // Organizações disponíveis para hunters
  const availableHunters = React.useMemo(() => {
    const selectedHunterIds = huntersFields.map((field) => field.id);
    
    return allClients
      .filter((client) => client.id !== clientId && !selectedHunterIds.includes(client.id))
      .map((client) => ({
        value: client.id,
        label: client.name,
      }));
  }, [allClients, huntersFields, clientId]);

  // Limpar level_hierarchical quando o tipo mudar para affinity
  const typeClient = projectForm.watch("type_client");
  const { setValue } = projectForm;
  React.useEffect(() => {
    if (typeClient === "affinity") {
      setValue("level_hierarchical", undefined, { shouldValidate: false });
    }
  }, [typeClient, setValue]);


  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                navigate(`/clients`, {
                  state: { openClientId: clientId },
                })
              }
              size="sm"
              className="p-3 hover:bg-muted/50 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Criar Projeto
            </h1>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {client?.name
              ? `Crie um novo projeto para ${client.name}`
              : "Crie um novo projeto para este parceiro"}
          </p>
        </div>
      </div>

      <Form {...projectForm}>
        <form
          onSubmit={projectForm.handleSubmit(
            handleSubmit,
            (errors) => {
              console.error("Erros de validação do formulário:", errors);
              toast({
                title: t("generic.error"),
                description: "Por favor, corrija os erros no formulário",
                variant: "destructive",
              });
            }
          )}
          className="space-y-6"
        >
          {/* Informações do Projeto */}
          <div className="space-y-4 p-6 bg-background border rounded-lg">
            <h2 className="text-lg font-semibold">Informações do Projeto</h2>
            
            <FormField
              control={projectForm.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Projeto</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Digite o nome do projeto"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={projectForm.control}
              name="type_client"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Projeto</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo de projeto" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="affinity">Afinidade</SelectItem>
                      <SelectItem value="affiliate">Afiliado</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Campo de níveis - apenas para projetos afiliados */}
            {projectForm.watch("type_client") === "affiliate" && (
              <FormField
                control={projectForm.control}
                name="level_hierarchical"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Níveis</FormLabel>
                    <Select
                      value={field.value?.toString() || "1"}
                      onValueChange={(value) => {
                        const numValue = parseInt(value, 10);
                        if (!isNaN(numValue) && numValue > 0) {
                          field.onChange(numValue);
                        }
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a quantidade de níveis" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="3">3</SelectItem>
                        <SelectItem value="4">4</SelectItem>
                        <SelectItem value="5">5</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* Configuração do Projeto */}
          <div className="space-y-6 p-6 bg-white border rounded-lg">
            <div className="bg-primary text-primary-foreground px-4 py-3 rounded-md text-center font-medium">
              Configuração do Projeto
            </div>

            {/* 1. Hierarquia Comercial */}
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-base font-semibold">1. Hierarquia Comercial</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure a hierarquia comercial para definir porcentagem de comissão dos usuários
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (availableUsers.length === 0) {
                      toast({
                        title: "Aviso",
                        description: "Não há mais usuários disponíveis para adicionar",
                        variant: "default",
                      });
                      return;
                    }
                    appendUserCommercial({
                      id: "",
                      commission_percentage: 0,
                    });
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Usuário
                </Button>
              </div>

              {userCommercialsFields.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground border border-dashed rounded-lg bg-background">
                  Nenhum usuário comercial adicionado
                </div>
              ) : (
                <div className="space-y-3">
                  {userCommercialsFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="grid grid-cols-1 md:grid-cols-[1fr,200px,auto] gap-3 items-end p-3 border rounded-lg bg-background"
                    >
                      <FormField
                        control={projectForm.control}
                        name={`user_commercials.${index}.id`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Usuário Comercial</FormLabel>
                            <FormControl>
                              <Combobox
                                options={availableUsers}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Selecione um usuário"
                                emptyMessage="Nenhum usuário encontrado"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={projectForm.control}
                        name={`user_commercials.${index}.commission_percentage`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Percentual de Comissão</FormLabel>
                            <FormControl>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  placeholder="0"
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  value={field.value}
                                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                />
                                <span className="text-sm">%</span>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeUserCommercial(index)}
                        className="mb-1"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 2. Parceiros participantes do projeto */}
            <div className="space-y-3">
              <div>
                <h3 className="text-base font-semibold">2. Parceiros participantes do projeto</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Selecione os parceiros (empresas) que farão parte deste projeto
                </p>
              </div>
              <FormField
                control={projectForm.control}
                name="partner_participants"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Buscar parceiro..."
                        value={partnerSearch}
                        onChange={(e) => setPartnerSearch(e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const current = field.value || [];
                          const allSelected =
                            sortedPartners.length > 0 &&
                            sortedPartners.every((p) => current.some((v) => v.id === p.id));
                          if (allSelected) {
                            field.onChange(
                              current.filter((v) => !sortedPartners.some((p) => p.id === v.id))
                            );
                          } else {
                            const toAdd = sortedPartners.filter(
                              (p) => !current.some((v) => v.id === p.id)
                            );
                            field.onChange([...current, ...toAdd.map((p) => ({ id: p.id }))]);
                          }
                        }}
                      >
                        {sortedPartners.length > 0 &&
                        sortedPartners.every((p) => (field.value || []).some((v) => v.id === p.id))
                          ? "Desmarcar todos"
                          : "Selecionar todos"}
                      </Button>
                    </div>
                    <div className="rounded-md border max-h-60 overflow-auto space-y-1 p-2">
                      {sortedPartners.length === 0 && (
                        <p className="text-sm text-muted-foreground py-2 text-center">
                          Nenhum parceiro encontrado
                        </p>
                      )}
                      {sortedPartners.map((partner) => (
                        <div key={partner.id} className="flex items-center gap-2 px-1 py-1">
                          <Checkbox
                            id={`partner-${partner.id}`}
                            checked={(field.value || []).some((v) => v.id === partner.id)}
                            onCheckedChange={(checked) => {
                              const current = field.value || [];
                              if (checked) {
                                field.onChange([...current, { id: partner.id }]);
                              } else {
                                field.onChange(current.filter((v) => v.id !== partner.id));
                              }
                            }}
                          />
                          <label
                            htmlFor={`partner-${partner.id}`}
                            className="text-sm cursor-pointer select-none"
                          >
                            {partner.name}
                          </label>
                        </div>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* 3. Hunter */}
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-base font-semibold">3. Hunter</h3>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (availableHunters.length === 0) {
                      toast({
                        title: "Aviso",
                        description: "Não há mais organizações disponíveis para adicionar",
                        variant: "default",
                      });
                      return;
                    }
                    appendHunter({
                      id: "",
                    });
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Hunter
                </Button>
              </div>

              {huntersFields.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground border border-dashed rounded-lg bg-background">
                  Nenhum hunter adicionado
                </div>
              ) : (
                <div className="space-y-3">
                  {huntersFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="grid grid-cols-[1fr,auto] gap-3 items-end p-3 border rounded-lg bg-background"
                    >
                      <FormField
                        control={projectForm.control}
                        name={`hunters.${index}.id`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Organização</FormLabel>
                            <FormControl>
                              <Combobox
                                options={availableHunters}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Selecione uma organização"
                                emptyMessage="Nenhuma organização encontrada"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeHunter(index)}
                        className="mb-1"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end gap-4 pt-8 pb-12">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate(`/clients`, {
                  state: { openClientId: clientId },
                })
              }
              disabled={isCreating}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? "Criando..." : "Criar Projeto"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

